import { FilterDimensionStatus } from '@Portal/models/smartFilters/FilterDimensionStatus';
import { SmartFilter } from '@Portal/models/smartFilters/SmartFilter';
import { SmartFilterDataType } from '@Portal/models/smartFilters/SmartFilterDataType';
import { NoResponseVariation } from '@Shared/types/portal/NoResponseVariation';
import { DateTimePeriod } from './types/portal/DateTimePeriod';
import { OrderByOperator } from './types/portal/OrderByOperator';
import { ProcessFlowQueue, ProcessflowActionHistoryResponse, ProcessflowActionType } from './types/portal/ProcessFlow';
import {
  CustomerCentreStageDataSource,
  DashboardType,
  FrontlineDashboardDataSource,
  SrgCustomerBoardDataSource,
  VisualType,
  ReportingDataSource,
} from './types/portal/dashboard';
import { FilterResult } from './utils/types';

export const UNIVERSAL_ROLE_ADMIN = 'Customer Centre Stage-administrator';
export const RESONATE_COLOR = '#5a0c21';
export const RESONATE_SECONDARY_COLOR = '#ff8d00';
export const MOBILE_SCREEN_SIZE = 576;
export const DATE_FORMAT = 'DD MMM YYYY';
export const DATETIME_FORMAT = `${DATE_FORMAT} HH:mm`;
export const DATE_FORMAT_WITH_DAYOFWEEK = `ddd, ${DATE_FORMAT}`;
export const LOCALSTORAGE_EXISTING_SESSION_ID = 'vue-existingSessionId';
export const DATE_FILTER_KEY = '__resonate_date_filter__';
// Referenced constants from PulsePortal, avoid changing these as they must be the same
export const NAVBAR_BUTTON_CONTAINER_ID = 'robyn-navbar-button-container';
export const ANGULAR_ROUTECHANGESUCCESS = 'angular_$routeChangeSuccess';

export const DEFAULT_CCS_DATA_SOURCE: CustomerCentreStageDataSource = {
  topVisualDateFilter: 'Last 7 Days',
  bottomVisualsDateFilter: 'Last 30 Days',
  bottomLayoutDetails: [
    { visual: 'NpsDial', width: 3, type: VisualType.GaugeChart },
    { visual: 'ProgramDrivers', width: 3, type: VisualType.MeasureList },
    { visual: 'NpsTrends', width: 6, type: VisualType.Trends },
  ],
} as CustomerCentreStageDataSource;

export const DEFAULT_FRONTLINE_DATA_SOURCE: FrontlineDashboardDataSource = {
  dateFilterType: 'Default',
  filterKeys: {},
  type: DashboardType.Frontline,
  defaultSmartFilters: [
    new SmartFilter('Svy_Data_DateLastResponded_OnAest', SmartFilterDataType.DateTimePeriod, DateTimePeriod.Last30Days),
  ],
  favouritesModuleId: 'Favourites',
  isSocial: false,
  name: 'FrontlineDashboard',
  smartDataModuleId: 'SmartData_CX',
  smartFilterTags: [],
  smartDataTags: [],
  visualTypes: {
    TopBanner: VisualType.FrontlineTopBanner,
    PrimaryMetric: VisualType.FrontlinePrimaryMetric,
    WorkoutDrivers: VisualType.DriverGauges,
    Trends: VisualType.Trends,
    ProgramDriverGauges: VisualType.ProgramDriverGauges,
    ProgramBreakdown: VisualType.FrontlineProgramBreakdown,
    ProcessFlowFeed: VisualType.ProcessFlowFeed,
    Coaching: VisualType.SmartInsights,
    WinsAndLosses: VisualType.SmartInsights,
  },
  primaryMetricPopupTitle: 'Relationship Program',
  driversPopupTitle: 'CX Muscles',
  driversPopupSubtitle: 'Percentage of respondents that agree with driver questions.',
};

export const DEFAULT_SOCIAL_DATASOURCE: any = {
  dateFilterType: 'Default',
  filterKeys: {},
  type: DashboardType.Social,
  defaultSmartFilters: [
    new SmartFilter('Svy_Data_DateLastResponded_OnAest', SmartFilterDataType.DateTimePeriod, DateTimePeriod.Last30Days),
  ],
  favouritesModuleId: 'Favourites',
  isSocial: true,
  name: 'ReviewsManager',
  smartDataModuleId: 'GoogleReviewsSmartData',
  smartFilterTags: [],
  smartDataTags: [],
  visualTypes: {
    ReviewsGenerated: VisualType.SocialReviewsGenerated,
    ReviewRatings: VisualType.SocialReviewRatings,
    ReviewFeed: VisualType.SocialReviewFeed,
    ReviewReplies: VisualType.SocialReviewReplies,
  },
};

export const DEFAULT_CUSTOMERBOARD_DATA_SOURCE: SrgCustomerBoardDataSource = {
  dateFilterType: 'Default',
  filterKeys: {},
  defaultSmartFilters: [
    new SmartFilter('Svy_Data_DateLastResponded_OnAest', SmartFilterDataType.DateTimePeriod, DateTimePeriod.Last30Days),
  ],
  type: DashboardType.SrgCustomerBoard,
  smartDataTags: [],
  favouritesModuleId: 'Favourites',
  isSocial: false,
  name: 'FrontlineDashboard',
  smartDataModuleId: 'SmartData_CX',
  smartFilterTags: [],
  visualTypes: {
    '0,0': VisualType.OneValueTile,
    '0,1': VisualType.OneValueTile,
    '0,2': VisualType.OneValueTile,
    '0,3': VisualType.DaysSinceLastResponse,
    '0,4': VisualType.MeasureTile,
    '0,5': VisualType.MeasureTile,
    '1,0': VisualType.SlicedMetricBreakdownTiles,
    '2,0': VisualType.SlicedMetricBreakdownTiles,
    '3,0': VisualType.TopNValuesTile,
    '3,1': VisualType.TopNValuesTile,
    '3,2': VisualType.TopValueByMeasureTile,
    '3,3': VisualType.TopValueByMeasureTile,
  },
};

export const DEFAULT_REPORTING_DATASOURCE: ReportingDataSource = {
  dateFilterType: 'Default',
  filterKeys: {},
  defaultSmartFilters: [
    new SmartFilter('Svy_Data_DateLastResponded_OnAest', SmartFilterDataType.DateTimePeriod, DateTimePeriod.Last30Days),
  ],
  type: DashboardType.Reporting,
  isSocial: false,
  name: 'Reporting',
  smartDataModuleId: 'SmartData_CX',
  smartFilterTags: [],
  visualTypes: {
    Favourites: VisualType.Favourites,
    ImprovedLikeForLikeReport: VisualType.ImprovedLikeForLikeReport,
  },
  smartDataTags: [],
  favouritesModuleId: '',
};

export const PROCESSFLOW_ACTION_HISTORY_SAMPLE_DATA = {
  history: [
    {
      processflowActionHistoryId: 128,
      processflowObjectId: 363,
      actionType: ProcessflowActionType.AllocationAction,
      actionName: 'Assign',
      actionFriendlyName: 'Assign',
      fromState: 'None',
      toState: 'Assigned',
      actionedByUserId: 'de7218d1-0b1e-4b1a-8a95-e4c30508e3fc',
      actionedByUserName: 'test@resonatesolutions.com.au',
      properties: {
        actionComment: '@user help',
        allocatedToUserId: 'de7218d1-0b1e-4b1a-8a95-e4c30508e3fc',
        allocatedToUserName: 'user@resonatesolutions.com.au',
        mentionedUsers: ['de7218d1-0b1e-4b1a-8a95-e4c30508e3fc'],
      },
      createdAt: '2022-09-20T06:13:54.8129494+00:00',
      actionHistoryDescription: '<span>User performed the <b>Assign</b> action</span>',
      metadata: {
        actionedByUserInitial: 'U',
        actionedByUserFriendlyName: 'User',
        allocatedByUserFriendlyName: 'User',
        allocatedToUserFriendlyName: 'Test',
      },
      actionHistoryIconClass: 'fa fa-reply-all',
    },
  ],
} satisfies ProcessflowActionHistoryResponse;

export const DEFAULT_FEEDBACKCOMPONENT_FILTERS: FilterResult = {
  filters: {
    primaryMetricSmartFilter: {
      filterName: 'NPS Index',
      filterKey: 'Svy_Data_Union_NpsScore_Index',
      isSibling: false,
      filterDimensionValues: [
        {
          dimensionValue: '[Blank]',
          count: 16,
        },
        {
          dimensionValue: '-1',
          count: 47,
        },
        {
          dimensionValue: '0',
          count: 20,
        },
        {
          dimensionValue: '1',
          count: 63,
        },
      ],
      filterDataType: SmartFilterDataType.Number,
      filterDimensionStatus: FilterDimensionStatus.FullyReturned,
      dateFilterOptions: {},
    },
    textAnalyticsTopicsSmartFilter: {
      filterName: 'Themes',
      filterKey: 'Txt_Union_Data_S__Verbatim_Themes',
      isSibling: false,
      filterDimensionValues: [
        {
          dimensionValue: 'Android Testing',
          count: 1,
        },
        {
          dimensionValue: 'Client Nps Relationship',
          count: 1,
        },
        {
          dimensionValue: 'Client Nps Relationship Test',
          count: 1,
        },
        {
          dimensionValue: 'Customer Interaction',
          count: 1,
        },
        {
          dimensionValue: 'Customer Service Experience',
          count: 1,
        },
        {
          dimensionValue: 'Detractor Score',
          count: 1,
        },
        {
          dimensionValue: 'Good Environment',
          count: 1,
        },
        {
          dimensionValue: 'Good Product',
          count: 1,
        },
        {
          dimensionValue: 'Good Products',
          count: 1,
        },
        {
          dimensionValue: 'Good Service',
          count: 3,
        },
        {
          dimensionValue: 'Good Support',
          count: 1,
        },
        {
          dimensionValue: 'Innovative Product',
          count: 1,
        },
        {
          dimensionValue: 'Quite Happy',
          count: 1,
        },
        {
          dimensionValue: 'Really Friendly',
          count: 1,
        },
        {
          dimensionValue: 'Really Helpful',
          count: 1,
        },
        {
          dimensionValue: 'Really Quick',
          count: 1,
        },
        {
          dimensionValue: 'Reminder Testing',
          count: 1,
        },
        {
          dimensionValue: 'Staff Behaviour',
          count: 1,
        },
        {
          dimensionValue: 'Survey Change',
          count: 2,
        },
        {
          dimensionValue: 'Test Asmita',
          count: 1,
        },
      ],
      filterDataType: SmartFilterDataType.String,
      filterDimensionStatus: FilterDimensionStatus.PartiallyReturned,
      dateFilterOptions: {},
    },
  },
};

export const DEFAULT_PROCESSFLOW_QUEUE_DATA: ProcessFlowQueue = {
  view: {
    name: 'Default - Summary View',
    type: 0,
    summaryTemplate: '/dist/partials/processflow/templates/multipleprograms/v2/summary.html',
    recordsPerPage: 7,
    isDefault: true,
  },
  hasExport: false,
  smartDataModuleId: 'SmartData_CX',
  name: 'Pending',
  defaultOrderBy: {
    fieldKey: 'Svy_Data_DateTimeLastResponded_AtAest',
    operator: 1,
    operatorAsString: OrderByOperator.DESC,
  },
  smartDataTags: ['PF_Card_Summary'],
  smartFilterTags: ['PF_Filter'],
  favouritesModuleId: 'Favourites',
  defaultSmartFilters: [],
  isSocial: false,
  dateFilterType: 'Default',
  ignoreClaimsForFilters: false,
};

export const BLANK_VALUE = '[Blank]';

export const NO_RESPONSE_VARIATIONS: NoResponseVariation[] = [
  {
    name: 'Default',
    trackingName: 'No Feedback Matched',
    headerMessage: 'No feedback matched.',
    bodyMessage: 'We did not find any feedback matched within the selected date range or with the filter criteria.',
    image: 'UnmatchedPuzzle',
    footerHeadingMessage: "Here's what you can do:",
    footerDotPoints: [
      'Change the date range and reduce some filters.',
      'Try searching with another phrase if you are looking for specific feedback.',
    ],
    colour: '#A16D6B',
    isSocial: false,
  },
  {
    name: 'NoInitialOrInProgress',
    trackingName: 'No Pending Or In Progress',
    headerMessage: "There's nothing on your plate!",
    bodyMessage:
      "You've actioned all {currentQueue} customer feedback. Do you want to go through some more of the recent feedback?",
    image: 'EmptyPlate',
    buttonText: 'Check {nextQueue}',
    colour: '#8021DA',
    isSocial: false,
  },
  {
    name: 'NoInitialSomeInProgress',
    trackingName: 'No New Feedback',
    headerMessage: 'High five!',
    bodyMessage: "You've reviewed everything in {currentQueue}. Do you want to check some {nextQueue} feedback?",
    image: 'HighFive',
    buttonText: 'Check {nextQueue} Feedback',
    colour: '#0C8A65',
    isSocial: false,
  },
  {
    name: 'SomeInitialNoInProgress',
    trackingName: 'Nothing In Progress',
    headerMessage: 'Hurray! Time for a break!',
    bodyMessage: "You've responded to the {currentQueue} feedback. Do you want to pick up some more in {nextQueue}?",
    image: 'CupOfCoffee',
    buttonText: 'Check {nextQueue} Feedback',
    colour: '#0C8A65',
    isSocial: false,
  },
  {
    name: 'SomeInitialOrInProgressNoFinal',
    trackingName: 'No Completed Feedback',
    headerMessage: 'Nothing to see here at the moment.',
    bodyMessage:
      "It seems that you're still following up on some feedback. Do you want to check some {nextQueue} feedback?",
    image: 'Binocular',
    buttonText: 'Check {nextQueue} Feedback',
    colour: '#8021DA',
    isSocial: false,
  },
  {
    name: 'FlyingCardsTooHeavy',
    trackingName: 'Flying Cards Too Heavy',
    headerMessage: 'Too heavy',
    bodyMessage:
      'Loading more than {cardLimitMax}  records in this view is not recommended. Try adding a few relevant filters or reduce the date range to limit the chosen data to less than {cardLimitMax}.',
    image: 'FlyingCardsTooHeavy',
    buttonText: '',
    colour: '#8021DA',
    isSocial: false,
  },
  {
    name: 'FlyingCardsABitHeavy',
    trackingName: 'Flying Cards A Bit Heavy',
    headerMessage: 'A bit heavy',
    bodyMessage:
      'Loading a large amount of data in this view may slow down your browser. Try adding a few relevant filters or reduce the date range to limit the chosen data to less than {cardLimitMax}.',
    image: 'FlyingCardsABitHeavy',
    buttonText: 'Load all {recordCount} records',
    colour: '#8021DA',
    isSocial: false,
  },
  {
    name: 'SocialCX-NoReviews',
    trackingName: 'SocialCX No Reviews',
    headerMessage: 'No reviews to see here',
    bodyMessage: 'We did not find any reviews within the selected date range or filter criteria.',
    image: 'FolderWithStars',
    footerHeadingMessage: 'See more reviews by:',
    footerDotPoints: ['Changing the date range', 'Removing some filters'],
    colour: '#C73362',
    isSocial: true,
  },
  {
    name: 'SocialCX-NoUnread',
    trackingName: 'SocialCX No Unread',
    headerMessage: 'You are up to date!',
    bodyMessage: 'You have read all your reviews. You might want to reply to your reviews in Awaiting Replies.',
    image: 'Tick',
    buttonText: 'Check Reviews in Awaiting Replies',
    footerHeadingMessage: 'You can check for more unread reviews by:',
    footerDotPoints: ['Changing the date range', 'Removing some filters'],
    colour: '#C73362',
    isSocial: true,
    // social variations that have links need to have another field for which tab to go to
    nextTab: 'Awaiting Replies',
  },
  {
    name: 'SocialCX-NoPendingReplies',
    trackingName: 'SocialCX No Pending Replies',
    headerMessage: 'You have replied to all reviews!',
    bodyMessage: 'No one is waiting for your reply. Do you want to check all your reviews?',
    image: 'Clipboard',
    buttonText: 'Check Reviews in All Reviews',
    footerHeadingMessage: 'You can check for more unreplied reviews by:',
    footerDotPoints: ['Changing the date range', 'Removing some filters'],
    colour: '#C73362',
    isSocial: true,
    // social variations that have links need to have another field for which tab to go to
    nextTab: 'All Reviews',
  },
  {
    name: 'SocialCX-NoReviewsGenerated',
    trackingName: 'SocialCX No Reviews Generated',
    headerMessage: 'Stay tuned as Review Amplifier grows your business!',
    bodyMessage:
      'We did not find any reviews generated from Review Amplifier within the selected date range or filter criteria.',
    image: 'ResonateGoogleCog',
    buttonText: 'Check Reviews with Awaiting Replies',
    footerHeadingMessage: 'See more reviews generated from Amplifier by:',
    footerDotPoints: ['Changing the date range', 'Removing some filters'],
    colour: '#C73362',
    isSocial: true,
    // social variations that have links need to have another field for which tab to go to
    nextTab: 'Awaiting Replies',
  },
  {
    name: 'SocialCX-RefreshingReviews',
    trackingName: 'SocialCX Refreshing Reviews',
    headerMessage: 'Refreshing reviews',
    bodyMessage:
      'We are refreshing your data, so you can get a complete view of your reviews. Please return after a few minutes and reload the page.',
    image: 'SocialHourglass',
    colour: '#F4B400',
    // we don't use the social padding/margins
    isSocial: false,
  },
  {
    name: 'ReadyToDownloadExport',
    trackingName: 'Ready to be downloaded',
    headerMessage: 'Ready to be downloaded',
    bodyMessage:
      'Click on the button below to start the download. Once done, check in your downloads folder to find your export. Feel free to continue your navigation into the Resonate portal.',
    image: 'ReadyToDownloadExport',
    colour: '#92BAF0',
    isSocial: false,
    buttonText: 'Start Download',
  },
  {
    name: 'DownloadExportExpired',
    trackingName: 'Download link is expired',
    headerMessage: 'Download link is expired',
    bodyMessage:
      'Your download link has expired and your export file is no longer accessible. Please generate a new export file in My Data.',
    image: 'DownloadExportExpired',
    colour: '#991A42',
    isSocial: false,
  },
];
